import React, { useState, useContext, useEffect } from 'react';
import {IProps} from './types';
import * as S from './styles';
import { Context } from '@routes/private/Editor/Context';
import { AuthContext } from '@utils/DOIgnition/AuthContext';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import selectors from '@state/selectors';
import actions from '@state/actions';
import InputTags from '@components/InputTags';
import ComboBox from '@components/ComboBox';
import Button from '@components/Button';
import { IDesignAuth, IUserRequestAccess, PermissionEnum, UserData, UserPermission } from '@state/designs/types';
import Avatar from '@components/Avatar';
import BlackCheckIcon from '@assets/icons/blackCheckIcon';
import _ from 'lodash';

const ShareOption:React.FC<IProps> = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [emailsList, setEmailsList] = useState<string[]>([]);
  const [userPermissionRequired, setUserPermissionRequired] = useState<IUserRequestAccess>({});
  const [permission, setPermission] = useState<string>("can edit");
  const [message, setMessage] = useState<string>("");
  const [emailsListValid, setEmailsListValid] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [shareToken, setShareToken] = useState<string | undefined>(undefined);
  const [copyStatus, setCopyStatus] = useState(0);

  const { entityInformation, onWarningScreenChange } = useContext(Context);
  const { userData, userRequestAccess, setUserRequestAccess } = useContext(AuthContext);
  const designAuth = useSelector(selectors.designs.getDesignAuth);
  const shareTokenSelector = useSelector(selectors.auth.getShareToken);

  const permissionOptions : string[] = ['can edit', 'can view', 'remove'];
  const newInvitePermissionOptions : string[] = ['can edit', 'can view'];

  const updateDesignAuth = (designAuth: IDesignAuth) => {
    return new Promise((resolve, reject) => {
      dispatch(actions.designs.updateDesignAuth(designAuth, (result: any, error: any) => {
        if (error) {
          reject(error);
        } else {
          resolve(result);
        }
      }))
    })
  }

  const getShareToken = (type: string, id: string) => {
    return new Promise<string>((resolve, reject) => {
      dispatch(actions.auth.createShareToken(type, id, (result: any, error: any) => {
        if (error) {
          reject(error);
        } else {
          resolve(result);
        }
      }))
    })
  }

  const givePermission = () => {
    setEmailsList([...emailsList, userRequestAccess.email]);
    setUserRequestAccess({});
  }

  const scrollAndChangePermission = () => {
    setUserPermissionRequired(userRequestAccess);
    const toScroll = document.getElementById(`user-access-${userRequestAccess.email}`);
    if (toScroll) toScroll.scrollIntoView({behavior: 'smooth', block: 'center'});
  }

  useEffect(() => {
    if (entityInformation) {
      const askForAcessToken = async () => {
        try {
          const result = await getShareToken(entityInformation.entityType.toLowerCase(), entityInformation.id);
        } catch(error) {
          console.error(error);
        }
      }
      askForAcessToken();
    }
  }, []);

  useEffect(() => {
    if (shareTokenSelector) {
      setShareToken(`${window.location.protocol}//${window.location.host}/#share_token=${shareTokenSelector}`);
    }
  }, [shareTokenSelector]);

  useEffect(() => {    
    if (userRequestAccess.email) {
      if (designAuth?.usersPermission?.find(up => up.email === userRequestAccess.email)) scrollAndChangePermission();
      else givePermission();

      if (userRequestAccess.permission == "VIEW")
       setPermission(newInvitePermissionOptions[1]);

      localStorage.removeItem("requestAccessEmail");
      localStorage.removeItem("requestAccessPermission");
      history.push({ pathname: location.pathname });
    }
  }, [userRequestAccess, designAuth?.usersPermission]);

  const handleSendInvite = async (value: string) => {
    let newDesignAuth: IDesignAuth = {id: entityInformation.id};
    const permission = parseToPermissionEnum(value);
    if (permission !== PermissionEnum.NONE) {
      setIsSaving(true);
      try {
        const userDataInfo: UserData = {
          email: userData?.profile.email ? userData?.profile.email : "defaultuser@tangocode.com",
          name: userData?.profile.name ? userData?.profile.name : "Default User"
        };
        let newUsersPermission: UserPermission[] = [];
        let currentUsersPermission: UserPermission[] = [];
        emailsList.map(email => {
          newUsersPermission = [...newUsersPermission, {email: email, permission: permission}];
        })
        designAuth?.usersPermission?.map(uPermission => {
          const duplicatedIndex = newUsersPermission.findIndex(newUPermission => newUPermission.email === uPermission.email);
          if (duplicatedIndex === -1 && entityInformation?.createdBy.email !== uPermission.email) currentUsersPermission = [...currentUsersPermission, uPermission];
        });
        newDesignAuth.action = "ADD";
        newDesignAuth.message = message;
        newDesignAuth.from = userDataInfo;
        newDesignAuth.usersPermission = [...newUsersPermission, ...currentUsersPermission];
        await updateDesignAuth(newDesignAuth);
        cleanFields();
      } catch (error) {
        cleanFields();
        console.error(error);
      }
    }
  }

  const cleanFields = () => {
    setIsSaving(false);
    setEmailsListValid(false);
    setEmailsList([]);
    setMessage("");
  }

  const handleSelectOption = async (id: string, value: string) => {
    if (value === 'remove') {
      handleDelete(id);      
    }

    const permission = parseToPermissionEnum(value);
    if (permission != PermissionEnum.NONE && designAuth) {
      const userIndex = designAuth.usersPermission?.findIndex(uPermission => uPermission.email === id);
      if (userIndex !== undefined && userIndex >= 0) {
        setIsSaving(true);
        try {
          let newDesignAuth = _.cloneDeep(designAuth);
          if (newDesignAuth.usersPermission) {
            if (newDesignAuth.usersPermission[userIndex].permission === permission) {
              setIsSaving(false);
              return;
            }
            newDesignAuth.usersPermission[userIndex].permission = permission;
          }
          const userDataInfo: UserData = {
            email: userData?.profile.email ? userData?.profile.email : "defaultuser@tangocode.com",
            name: userData?.profile.name ? userData?.profile.name : "Default User"
          };
          newDesignAuth.action = "UPDATE";
          newDesignAuth.from = userDataInfo;
          newDesignAuth.message = "";
          await updateDesignAuth(newDesignAuth);
          setIsSaving(false);
          if (newDesignAuth.usersPermission && newDesignAuth.usersPermission[userIndex].email === userPermissionRequired.email) {
            if(newDesignAuth.usersPermission[userIndex].permission === PermissionEnum.EDIT) {
              setUserPermissionRequired({});
              setUserRequestAccess({});
            }
          }
        } catch (error) {
          setIsSaving(false);
          console.error(error);
        }
      }
    }
  }

  const closeWarningScreen = () => {
    onWarningScreenChange(false, "", ``, "", undefined, "", "", []);
  }

  const handleDelete = (email: string) => {
    props.setOpenOptions(false);    
    onWarningScreenChange(
      true, `Remove User's Permissions`,
      `Are you sure you would like to remove this user's permissions.\n\nNote: If they have access to the shareable link, they will still be able to view the file`,
      "CANCEL", () => {handleRemoveOption(email)}, "REMOVE", "", [], () => {handleCancelOption()} 
    );
  }

  const handleCancelOption = async () => {
    props.setOpenOptions(true);    
    closeWarningScreen();
  }

  const handleRemoveOption = async (email: string) => {
    if (designAuth) {
      const userIndex = designAuth.usersPermission?.findIndex(uPermission => (uPermission.email === email));
      if (userIndex !== undefined && userIndex >= 0) {
        setIsSaving(true);
        try {
          let newDesignAuth = _.cloneDeep(designAuth);
          if (newDesignAuth.usersPermission) {
            newDesignAuth.usersPermission.splice(userIndex,1);
          }
          const userDataInfo: UserData = {
            email: userData?.profile.email ? userData?.profile.email : "defaultuser@tangocode.com",
            name: userData?.profile.name ? userData?.profile.name : "Default User"
          };
          newDesignAuth.action = "DELETE";
          newDesignAuth.from = userDataInfo;
          newDesignAuth.message = "";
          await updateDesignAuth(newDesignAuth);
          setIsSaving(false);
        } catch (error) {
          setIsSaving(false);
          console.error(error);
        }
      }
    }
    props.setOpenOptions(true);    
    closeWarningScreen();
  }

  const handleCopyLink = () => {
    if (shareToken !== undefined) {
      navigator.clipboard.writeText(shareToken)
        .then(() => {
          setCopyStatus(1);
          setTimeout(() => {
            setCopyStatus(2);
          }, 3000);
        }, (err) => {
          console.error(err);
        });
    }
  }

  const parseFromPermissionEnum = (uPermission: PermissionEnum) => {
    if (uPermission === PermissionEnum.EDIT) return permissionOptions[0];
    if (uPermission === PermissionEnum.VIEW) return permissionOptions[1];
    if (uPermission === PermissionEnum.NONE) return permissionOptions[2];
    return permissionOptions[0];
  }

  const parseToPermissionEnum = (value: string) => {
    if (value === permissionOptions[0]) return PermissionEnum.EDIT;
    if (value === permissionOptions[1]) return PermissionEnum.VIEW;
    if (value === permissionOptions[2]) return PermissionEnum.NONE;
    return PermissionEnum.EDIT;
  }

  return (                            
      <S.Root>
      <S.ScrollContainer>
        <S.Row>
          <InputTags
          list={emailsList}
          onUpdateList={setEmailsList}
          type={'email'}
          validateType={true}
          placeholder={"Enter email(s), comma separated"}
          onValidateList={(value: boolean) => setEmailsListValid(value) }/>
          <ComboBox 
            id="permissions"
            placeholder="can edit"
            options={newInvitePermissionOptions}
            required={false}                
            disabled={isSaving}
            inputDisabled={true}
            position={"bottom"}    
            chooseOption={(id:string, value:string) => setPermission(value)}
            value={permission}
            light={true}
            style={{ 'paddingRight':'0px'}}
            containerStyle={{ 'width': '131px', 'marginLeft':'17px', 'height': '48px'}}
            boxContainerStyle={{'height': '48px'}}
          />
        </S.Row>
        <S.Row>
          { emailsList.length>0 &&
          <S.TextArea
            value={message}
            rows={6}
            placeholder={'Enter message (optional)'}
            onChange={(e: any) => setMessage(e.target.value)}
            disabled={isSaving}
          />}
        </S.Row>
        <Button
            icon="" 
            label="SEND INVITE"
            style={{zIndex: 10,hoverBackgroundColor: "#344a70", backgroundColor: "#366cc8", width: 143, height: 36, boxSizing: 'border-box', marginTop: '13px', marginLeft: '39px', marginBottom: '30px'}}
            labelStyle={{color: "white", fontWeight: '500'}}
            onClick={() => handleSendInvite(permission)}
            disabled={!emailsListValid || isSaving}
        />
        <S.Divider />
        <S.EditPermissionsContainer>
          <S.UserPermissionRow>
            <S.UserRow>
              <Avatar text={entityInformation?.createdBy.email} />
              <S.UserName>
                {`${entityInformation?.createdBy.email}${entityInformation?.createdBy.email === userData?.profile.email ? ` (You)` : ``}`}
              </S.UserName>
            </S.UserRow>
            <S.Owner>
              Owner
            </S.Owner>
          </S.UserPermissionRow>
          <S.UsersPermissionContainer>
            {designAuth?.usersPermission?.map((uPermission, index) => {
              return (
                <S.UserPermissionRow
                  key={uPermission.email}
                  id={`user-access-${uPermission.email}`}
                >
                  <S.UserRow>
                    <Avatar text={uPermission.email} />
                    <S.UserName requestedAccess={uPermission.email === userPermissionRequired.email} >
                      {uPermission.email}
                    </S.UserName>
                    {
                      uPermission.email === userPermissionRequired.email &&
                      <S.RequestedAccessLabel>
                        has requested {userPermissionRequired.permission ? userPermissionRequired.permission.toLowerCase() : ""} access
                      </S.RequestedAccessLabel>
                    }
                  </S.UserRow>
                  <ComboBox 
                    id={uPermission.email}
                    placeholder="can edit"
                    options={permissionOptions}
                    required={false}                
                    disabled={isSaving}
                    inputDisabled={true}
                    position={"bottom"}    
                    chooseOption={handleSelectOption}
                    value={parseFromPermissionEnum(uPermission.permission)}
                    light={true}
                    style={{'paddingRight': '0px'}}
                    containerStyle={{ 'width': '130px', 'height': '48px'}}
                    boxContainerStyle={{'height': '48px'}}
                  />
                </S.UserPermissionRow>
              );
            })}
          </S.UsersPermissionContainer>
        </S.EditPermissionsContainer>
        <S.Divider />
        <S.TopCopyLinkMessage>
          Anyone with the link can view
        </S.TopCopyLinkMessage>
        <S.CopyLinkContainer>
          <Button
            label="COPY LINK"
            style={{zIndex: 10,hoverBackgroundColor: "#344a70", backgroundColor: "#366cc8", width: 143, height: 36, boxSizing: 'border-box', marginTop: '13px', marginLeft: '39px', marginBottom: '10px'}}
            labelStyle={{color: "white", fontWeight: '500'}}
            onClick={handleCopyLink}
            disabled={shareToken === undefined}
          />
          { copyStatus >= 1 &&
            <S.CopyMessageContainer
              topValue={copyStatus === 1 ? `14.5px` : `13px`}
            >
              { copyStatus === 1 &&
                <S.CheckIconContainer>
                  <BlackCheckIcon />
                </S.CheckIconContainer>
              }
              <S.CopyMessage>
                { copyStatus === 1 ? `Link Copied` : `Users with edit permissions will need to have the a DealerOn account with the same email place in this permission.`}
              </S.CopyMessage>
            </S.CopyMessageContainer>
          }
        </S.CopyLinkContainer>
      </S.ScrollContainer>
      </S.Root>
  );
}

export default ShareOption;